import './LabelingRulesTable.scss';
import { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { MdContentCopy, MdDelete, MdMode, MdReorder } from 'react-icons/md';
import {
  isBillingSegmentName,
  SegmentationMappingLabelRule
} from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import { IconButton } from '@Components/IconButton';
import { InfoTooltip } from '@Components/InfoTooltip';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { FrozenColumnsTable } from '@Components/FrozenColumnsTable';
import { CellRenderer, CornerLocation, Header } from '@Components/StickyTable';
import { Placement } from '@Components/designConstants';
import {
  BillingMappingLabelRule,
  CustomSegmentsData,
  SystemGeneratedSegmentNames
} from './segmentation-mapping-constants';
import { buildIfStatement, formatDateRange } from './segmentationMappingUtils';

interface RuleRowData extends Omit<SegmentationMappingLabelRule, 'filterTokens'> {
  setBillingSegment?: string;
  setBillingSegmentValue?: string;
  startDate?: string;
  endDate?: string | null;
  filterTokens: string | { label: string }[];
}

interface LabelingRulesTableProps {
  rules: SegmentationMappingLabelRule[];
  customSegments: CustomSegmentsData[] | undefined;
  activeTab: string;
  onRuleDelete?: (rule: SegmentationMappingLabelRule) => void;
  onLiveToggle?: (rule: SegmentationMappingLabelRule) => void;
  setModalShow?: (show: boolean) => void;
  setRuleToEdit?: (rule: SegmentationMappingLabelRule | null) => void;
  setRuleToCopy?: (rule: SegmentationMappingLabelRule | null) => void;
  onRuleReorder?: (rules: SegmentationMappingLabelRule[] | BillingMappingLabelRule[]) => void;
}

interface LabelingRulesHeader {
  label: string | JSX.Element;
  field: string;
  flex: number;
  minFlexWidth?: number;
  renderer: (data: RuleRowData) => JSX.Element | string;
  name?: string;
}

export const LabelingRulesTable = ({
  rules,
  customSegments,
  activeTab,
  onRuleDelete = () => {},
  onLiveToggle = () => {},
  setModalShow = () => {},
  setRuleToEdit = () => {},
  setRuleToCopy = () => {},
  onRuleReorder = () => {}
}: LabelingRulesTableProps) => {
  const [innerWidth, setInnerWidth] = useState<number>(window.innerWidth);
  useEffect(() => {
    const onResize = () => setInnerWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const leftWidth = (1 / 24) * innerWidth;
  const isReorderable = activeTab !== 'all';

  const simpleCustomSegments = useMemo(
    () =>
      customSegments?.filter(segment => {
        const systemNames = Object.values(SystemGeneratedSegmentNames).map(name => String(name));
        return !systemNames.includes(segment.segmentName);
      }),
    [customSegments]
  );

  const findSegmentName = useCallback(
    (segmentId: number): string =>
      customSegments?.find(segment => Number(segment.segmentId) === segmentId)?.segmentName || '',
    [customSegments]
  );

  const findSegmentValue = useCallback(
    (valueId: number): string =>
      simpleCustomSegments
        ?.flatMap(segment => segment.values)
        .find(value => Number(value.valueId) === valueId)?.valueName || '',
    [simpleCustomSegments]
  );

  const handleRuleEditClick = useCallback(
    (rule: RuleRowData) => {
      setModalShow(true);
      setRuleToEdit(rule);
    },
    [setModalShow, setRuleToEdit]
  );

  const handleRuleCopyClick = useCallback(
    (rule: RuleRowData) => {
      setRuleToCopy({
        ...rule,
        id: undefined,
        name: ''
      } as unknown as SegmentationMappingLabelRule);
    },
    [setRuleToCopy]
  );

  const handleRuleDeleteClick = useCallback(
    (rule: RuleRowData) => {
      onRuleDelete(rule);
    },
    [onRuleDelete]
  );

  const rightTableHeaders: LabelingRulesHeader[] = useMemo(
    () => [
      {
        label: 'Rule ID',
        field: 'name',
        flex: 0.5,
        renderer: data => (
          <div className={cn('rule-id-cell', { active: isReorderable })}>
            {isReorderable && <MdReorder />}
            {data.name || '-'}
          </div>
        )
      },
      {
        label: 'Segment',
        field: 'setSegmentId',
        flex: 1,
        renderer: data =>
          data.setSegmentId ? findSegmentName(data.setSegmentId) : data.setBillingSegment || ''
      },
      {
        label: 'Segment Value',
        field: 'setSegmentValueId',
        flex: 1,
        renderer: data =>
          data.setSegmentValueId
            ? findSegmentValue(data.setSegmentValueId)
            : data.setBillingSegmentValue || ''
      },
      {
        label: 'If statement',
        field: 'filterTokens',
        flex: 1.5,
        renderer: data => {
          if (data.isAdvanced == null) {
            return '';
          }
          return (
            <div className='if-statement-cell'>
              <span className='if-statement-text'>
                {buildIfStatement(data.filterTokens, data.isAdvanced)}
              </span>
            </div>
          );
        }
      },
      ...(isBillingSegmentName(activeTab)
        ? [
            {
              label: 'Date Range',
              field: 'dateRange',
              flex: 1,
              minFlexWidth: 200,
              renderer: (data: RuleRowData) => (
                <div className='date-range-cell'>
                  {formatDateRange(data.startDate, data.endDate)}
                </div>
              )
            }
          ]
        : []),
      {
        label: 'Created',
        field: 'created',
        flex: 0.75,
        renderer: data => {
          const date = new Date(data.createdAt).toLocaleDateString();
          const newDate = date === 'Invalid Date' ? new Date().toLocaleDateString() : date;
          return (
            <span className='rule-metadata-cell'>
              {data.createdBy} {newDate}
            </span>
          );
        }
      },
      {
        label: 'Modified',
        field: 'modified',
        flex: 0.75,
        renderer: data => {
          const date = new Date(data.updatedAt).toLocaleDateString();
          const newDate = date === 'Invalid Date' ? new Date().toLocaleDateString() : date;
          return (
            <span className='rule-metadata-cell'>
              {data.lastUpdatedBy} {newDate}
            </span>
          );
        }
      },
      {
        label: (
          <span className='enabled-label'>
            Enabled
            <InfoTooltip placement={Placement.TOP_END}>
              Enable or disable this rule. Only enabled rules can be applied on the labeling page.
            </InfoTooltip>
          </span>
        ),
        field: 'isActive',
        flex: 0.1,
        renderer: data => (
          <div>
            <ToggleSwitch checked={data.isActive} onChange={() => onLiveToggle(data)} label='' />
          </div>
        )
      },
      {
        label: '',
        field: 'actions',
        flex: 1,
        renderer: data => (
          <div className='actions'>
            <IconButton onClick={() => handleRuleCopyClick(data)} title='Copy Rule'>
              <MdContentCopy />
            </IconButton>
            <IconButton onClick={() => handleRuleEditClick(data)} title='Edit Rule'>
              <MdMode />
            </IconButton>
            <IconButton onClick={() => handleRuleDeleteClick(data)} title='Delete Rule'>
              <MdDelete />
            </IconButton>
          </div>
        )
      }
    ],
    [
      activeTab,
      isReorderable,
      findSegmentName,
      findSegmentValue,
      handleRuleCopyClick,
      handleRuleDeleteClick,
      handleRuleEditClick,
      onLiveToggle
    ]
  );

  const customLeftRenderer: CellRenderer<Record<string, unknown>> = useCallback(
    ({ data, style = {}, classes = [] }) => (
      <div
        className={cn('left-cell-div', classes)}
        style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {isReorderable ? String(data?.rulePriority ?? '') : ''}
      </div>
    ),
    [isReorderable]
  );

  const [rightData, setRightData] = useState<{ originalRowIndex: number; content: unknown }[][]>(
    []
  );

  // Ensure the data passed into FrozenColumnsTable has the same number of columns as there are headers.
  const rightDataForTable = useMemo(() => {
    if (!rightData.length) {
      return rightData;
    }
    const headerCount = rightTableHeaders.length;
    const rowColumnCount = rightData[0]?.length ?? 0;
    return rowColumnCount === headerCount ? rightData : [];
  }, [rightData, rightTableHeaders.length]);

  const customCornerRenderer = useCallback(
    (corner: CornerLocation) => {
      if (corner === 'nw') {
        return (
          <div
            className='sticky-table-cell'
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
              height: '100%'
            }}
          >
            <div className={cn('left-cell-header', { inactive: !isReorderable })}>
              Rule Priority
            </div>
          </div>
        );
      }
      return <div />;
    },
    [isReorderable]
  );

  const leftDataFields = ['rulePriority'];

  const noRowsRenderer = useCallback(
    () => (rules.length === 0 ? <div className='no-rows'>No rules created yet.</div> : <div />),
    [rules.length]
  );

  const [localRules, setLocalRules] = useState<SegmentationMappingLabelRule[]>(rules);
  const localRulesRef = useRef<SegmentationMappingLabelRule[]>(rules);

  useEffect(() => {
    setLocalRules(rules);
    localRulesRef.current = rules;
  }, [rules]);

  const moveRow = useCallback((dragIndex: number, hoverIndex: number) => {
    const updated = [...localRulesRef.current];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(hoverIndex, 0, moved);

    const reordered = updated.map((rule, idx) => ({
      ...rule,
      rulePriority: idx + 1
    }));

    localRulesRef.current = reordered;
    setLocalRules(reordered);
  }, []);

  const handleDragEnd = useCallback(() => {
    onRuleReorder(localRulesRef.current);
  }, [onRuleReorder]);

  return (
    <div className='labeling-rules-sticky-table'>
      <FrozenColumnsTable
        leftWidth={leftWidth}
        leftRenderer={customLeftRenderer}
        cornerRenderer={customCornerRenderer}
        rightTableHeaders={rightTableHeaders as unknown as Header[]}
        rightTableHeaderNames={rightTableHeaders.map(header => header.name ?? '')}
        originalRightData={localRules as unknown as Record<string, unknown>[]}
        leftDataFields={leftDataFields}
        rightData={rightDataForTable}
        setRightData={setRightData}
        noRowsRenderer={noRowsRenderer}
        moveRow={isReorderable ? moveRow : undefined}
        onDragEnd={isReorderable ? handleDragEnd : undefined}
        topHeight={60}
        isDraggable={isReorderable}
      />
    </div>
  );
};
